import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

const CATEGORIES = [
  { value: "general", label: { uk: "Загальне", en: "General", ru: "Общее" } },
  { value: "bug", label: { uk: "Повідомити про баг", en: "Bug report", ru: "Сообщить об ошибке" } },
  { value: "feature", label: { uk: "Пропозиція функції", en: "Feature request", ru: "Предложение функции" } },
  { value: "feedback", label: { uk: "Фідбек", en: "Feedback", ru: "Фидбек" } },
  { value: "privacy", label: { uk: "Приватність і дані", en: "Privacy & data", ru: "Приватность и данные" } },
  { value: "partnership", label: { uk: "Партнерство / преса", en: "Partnership / press", ru: "Партнёрство / пресса" } },
];

const UI = {
  title: { uk: "Зв'яжіться з нами", en: "Get in touch", ru: "Свяжитесь с нами" },
  subtitle: {
    uk: "MealPill робить одна людина. Це означає, що я особисто читаю кожен лист.",
    en: "MealPill is built by one person. That means I read every email myself.",
    ru: "MealPill делает один человек. Это значит, что я лично читаю каждое письмо.",
  },
  sendAMessage: { uk: "Надіслати повідомлення", en: "Send a message", ru: "Отправить сообщение" },
  sentTitle: { uk: "Дякую — повідомлення надіслано.", en: "Thanks - message sent.", ru: "Спасибо — сообщение отправлено." },
  sentBody: {
    uk: "Я особисто читаю кожне повідомлення і зазвичай відповідаю протягом 24–48 годин.",
    en: "I read every message personally and usually reply within 24-48 hours.",
    ru: "Я лично читаю каждое сообщение и обычно отвечаю в течение 24–48 часов.",
  },
  sendAnother: { uk: "Надіслати ще одне", en: "Send another", ru: "Отправить ещё одно" },
  topic: { uk: "Тема звернення", en: "Topic", ru: "Тема обращения" },
  subject: { uk: "Заголовок", en: "Subject", ru: "Заголовок" },
  subjectPh: { uk: "Коротко про суть", en: "Short summary", ru: "Коротко о сути" },
  message: { uk: "Повідомлення", en: "Message", ru: "Сообщение" },
  messagePh: { uk: "Опишіть ваше питання чи проблему", en: "What is on your mind?", ru: "Опишите ваш вопрос или проблему" },
  errorText: {
    uk: "Щось пішло не так. Спробуйте ще раз або напишіть нам на пошту нижче.",
    en: "Something went wrong. Please try again or email us below.",
    ru: "Что-то пошло не так. Попробуйте ещё раз или напишите нам на почту ниже.",
  },
  sending: { uk: "Надсилаю...", en: "Sending...", ru: "Отправляю..." },
  sendMessage: { uk: "Надіслати", en: "Send message", ru: "Отправить" },
  emailUs: { uk: "Напишіть нам", en: "Email us", ru: "Напишите нам" },
  emailNote: {
    uk: "Відповідаю протягом 24–48 годин. Якщо щось термінове або зламане — додайте «терміново» в тему листа.",
    en: 'I respond within 24-48 hours. If something is urgent or broken, mention "urgent" in the subject line.',
    ru: "Отвечаю в течение 24–48 часов. Если что-то срочное или сломано — добавьте «срочно» в тему письма.",
  },
  topicsTitle: { uk: "Про що можна написати", en: "What you can write about", ru: "О чём можно написать" },
  bugTitle: { uk: "Повідомлення про баги", en: "Bug reports", ru: "Сообщения об ошибках" },
  bugBody: {
    uk: "Щось працює не так, як очікувалось. Вкажіть браузер/пристрій, що ви робили і що сталося.",
    en: "Something not working as expected. Include your browser/device, what you did, and what happened.",
    ru: "Что-то работает не так, как ожидалось. Укажите браузер/устройство, что вы делали и что произошло.",
  },
  featureTitle: { uk: "Пропозиції функцій", en: "Feature requests", ru: "Предложения функций" },
  featureBody: {
    uk: "Що насправді зробило б ваш тиждень легшим? Я особисто читаю всі пропозиції.",
    en: "What would actually make your week easier? I read all suggestions personally.",
    ru: "Что на самом деле сделало бы вашу неделю легче? Я лично читаю все предложения.",
  },
  betaTitle: { uk: "Фідбек", en: "Feedback", ru: "Фидбек" },
  betaBody: {
    uk: "Плани харчування не влучають, дивні рецепти, плутанина в UX. Усе корисно — навіть дрібні дратівливі моменти.",
    en: "Meal plans missing the mark, weird recipes, UX confusion. All useful - even tiny annoyances.",
    ru: "Планы питания не попадают в цель, странные рецепты, путаница в UX. Всё полезно — даже мелкие раздражающие моменты.",
  },
  privacyTitle: { uk: "Приватність і дані", en: "Privacy & data requests", ru: "Приватность и данные" },
  privacyBody: {
    uk: <>Хочете видалити акаунт, експортувати дані чи виправити інформацію. Деталі — у <Link to="/privacy" className="text-[#7B9E7F] underline">Політиці приватності</Link>.</>,
    en: <>Want to delete your account, export data, or correct information. See <Link to="/privacy" className="text-[#7B9E7F] underline">Privacy Policy</Link> for details.</>,
    ru: <>Хотите удалить аккаунт, экспортировать данные или исправить информацию. Подробности — в <Link to="/privacy" className="text-[#7B9E7F] underline">Политике конфиденциальности</Link>.</>,
  },
  partnerTitle: { uk: "Партнерства і преса", en: "Partnerships & press", ru: "Партнёрства и пресса" },
  partnerBody: {
    uk: "Ідеї співпраці, інтерв'ю, будь-що, що не підходить під категорії вище.",
    en: "Collaboration ideas, interviews, anything that does not fit above.",
    ru: "Идеи сотрудничества, интервью, всё, что не подходит под категории выше.",
  },
  backToMealPill: { uk: "← Назад до MealPill", en: "← Back to MealPill", ru: "← Назад в MealPill" },
  privacy: { uk: "Політика приватності", en: "Privacy Policy", ru: "Политика конфиденциальности" },
  terms: { uk: "Умови використання", en: "Terms of Service", ru: "Условия использования" },
};

export default function SupportPage() {
  const { user, api } = useAuth();
  const { lang } = useLanguage();
  const L = (o) => (o && o[lang]) || (o && o.en);
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const submit = async () => {
    if (!subject.trim() || !message.trim()) return;
    setStatus("sending");
    try {
      await api().post("/support", {
        subject: subject.trim(),
        message: message.trim(),
        category,
      });
      setStatus("sent");
      setSubject("");
      setMessage("");
      setCategory("general");
    } catch (e) {
      console.error(e);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#3D4035]">
      <header className="border-b border-black/5">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="text-[#7B9E7F] font-medium hover:opacity-80 transition">
            &larr; MealPill
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <h1
          className="text-4xl md:text-5xl font-serif mb-3"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {L(UI.title)}
        </h1>
        <p className="text-lg text-[#3D4035]/70 mb-12 leading-relaxed">
          {L(UI.subtitle)}
        </p>

        {/* In-app message form (logged-in users) */}
        {user && (
          <div className="bg-white border border-black/5 rounded-2xl p-8 mb-12 shadow-sm">
            <p className="text-xs text-[#3D4035]/60 mb-4 uppercase tracking-wide">{L(UI.sendAMessage)}</p>

            {status === "sent" ? (
              <div className="text-[15px] text-[#3D4035]/80">
                <p className="font-medium text-[#7B9E7F] mb-1">{L(UI.sentTitle)}</p>
                <p>{L(UI.sentBody)}</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-sm text-[#7B9E7F] hover:underline"
                >
                  {L(UI.sendAnother)}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#3D4035]/70 mb-1">{L(UI.topic)}</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7B9E7F]/40"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{L(c.label)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-[#3D4035]/70 mb-1">{L(UI.subject)}</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={L(UI.subjectPh)}
                    className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#7B9E7F]/40"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#3D4035]/70 mb-1">{L(UI.message)}</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder={L(UI.messagePh)}
                    className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-[15px] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#7B9E7F]/40"
                  />
                </div>

                {status === "error" && (
                  <p className="text-sm text-red-500">
                    {L(UI.errorText)}
                  </p>
                )}

                <button
                  onClick={submit}
                  disabled={status === "sending" || !subject.trim() || !message.trim()}
                  className="px-5 py-2.5 rounded-lg bg-[#7B9E7F] text-white text-[15px] font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? L(UI.sending) : L(UI.sendMessage)}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Email card */}
        <div className="bg-white border border-black/5 rounded-2xl p-8 mb-12 shadow-sm">
          <p className="text-xs text-[#3D4035]/60 mb-2 uppercase tracking-wide">{L(UI.emailUs)}</p>
          <a
            href="mailto:hello@mealpill.app"
            className="text-2xl md:text-3xl font-medium text-[#7B9E7F] hover:underline break-all"
          >
            hello@mealpill.app
          </a>
          <p className="mt-4 text-sm text-[#3D4035]/70 leading-relaxed">
            {L(UI.emailNote)}
          </p>
        </div>

        {/* Common topics */}
        <div className="space-y-6">
          <h2 className="text-xl font-medium">{L(UI.topicsTitle)}</h2>

          <div className="space-y-5 text-[15px] leading-relaxed">
            <div>
              <strong className="block mb-1">{L(UI.bugTitle)}</strong>
              <span className="text-[#3D4035]/80">
                {L(UI.bugBody)}
              </span>
            </div>

            <div>
              <strong className="block mb-1">{L(UI.featureTitle)}</strong>
              <span className="text-[#3D4035]/80">
                {L(UI.featureBody)}
              </span>
            </div>

            <div>
              <strong className="block mb-1">{L(UI.betaTitle)}</strong>
              <span className="text-[#3D4035]/80">
                {L(UI.betaBody)}
              </span>
            </div>

            <div>
              <strong className="block mb-1">{L(UI.privacyTitle)}</strong>
              <span className="text-[#3D4035]/80">
                {L(UI.privacyBody)}
              </span>
            </div>

            <div>
              <strong className="block mb-1">{L(UI.partnerTitle)}</strong>
              <span className="text-[#3D4035]/80">
                {L(UI.partnerBody)}
              </span>
            </div>
          </div>
        </div>

        <footer className="mt-20 pt-8 border-t border-black/10 text-sm text-[#3D4035]/60 flex flex-wrap gap-4 justify-between">
          <Link to="/" className="hover:text-[#3D4035] transition">
            {L(UI.backToMealPill)}
          </Link>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#3D4035] transition">
              {L(UI.privacy)}
            </Link>
            <Link to="/terms" className="hover:text-[#3D4035] transition">
              {L(UI.terms)}
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
